/*
  All settings related to websocket (telemetry) subscriptions in one place.

  Components only subscribe to telemetry while they are actually shown, see
  services/page.service.ts (visibility) and services/subscription-debug.ts (console debugging).
  Durations are in milliseconds.
*/
export const WEBSOCKET_CONFIG = {

  // Set to false to turn the automatic unsubscribing off completely: nothing is released when
  // it is hidden, off-screen or when the app is in the background. Only for testing, e.g. to
  // provoke "Too many updates!" errors from the server and see the retries.
  enabled: true,

  // When the server answers a subscription with an error (e.g. errorCode 34 "Too many updates!")
  // it stops sending data for that subscription. The subscription is then retried with a full
  // resubscription: the first retry waits retryDelay, every following retry waits twice as long
  // as the previous one, until maxRetries is reached and the entity is marked as failed.
  // The retries start over once a subscription has been working for longer than the longest wait.
  maxRetries: 5,
  retryDelay: 5 * 1000,

  // Number of websocket subscriptions that are allowed to stay alive even though the components
  // using them are hidden (other page, off-screen), so customers with few devices never have to
  // fetch everything again when navigating around. When more subscriptions than this are active,
  // hidden ones are released as configured below. Only backgroundDelay still applies.
  // A device card counts roughly 4 (its model, one per sensor and its plot), a building card 2.
  keepAliveLimit: 30,

  // Cards in a scrolling list (dashboard, building devices): how many pixels a card must be
  // outside the visible area of its scroll container before it counts as off-screen ...
  offscreenPixels: 4000,

  // ... and how long it must stay off-screen before its subscriptions are released.
  // Scrolling back within this time keeps everything as it is.
  offscreenDelay: 30 * 1000,

  // How long the app must be in the background (other browser tab, phone app paused) before
  // all subscriptions are released. Everything visible is subscribed again when the app returns,
  // so a short delay means fetching everything again every time the app is reopened.
  backgroundDelay: 5 * 60 * 1000,

  // When the last subscriber of an entity leaves (e.g. navigating to another page), the shared
  // data is kept alive this long before the websocket subscription is released, so quick
  // back-navigation and re-renders do not fetch everything again. Identical subscriptions (same
  // entity, keys and time settings) share one server subscription, which is handed over to the
  // next subscriber within this time as well.
  graceDelay: 3 * 1000,

  // websocket connection
  reconnectInterval: 2000,
  idleTimeout: 90000,
  maxPublishCommands: 10,

  // console debugging of subscriptions, see services/subscription-debug.ts
  // (can also be toggled at runtime with agDebugSubscriptions(true|false) in the console)
  debug: {
    enabled: false,
    // print a warning when more than this many subscriptions are active at the same time
    warnThreshold: 40,
    // delay before the summary table is printed after the last change
    summaryDelay: 1000
  }
}
